import { useEffect, useMemo, useRef, useState } from 'react';
import { generateYAxesWithColors } from 'src/utilities';

type UseChartOptionsType = {
  fromDate: Date;
  toDate: Date;
  debouncedExtendTimeframeOnZoomOutOrPan: (min: string, max: string) => void;
  tags: {}[];
  colors: string[];
  isMobile: boolean;
};

export const useChartOptions = ({
  fromDate,
  toDate,
  tags,
  colors,
  isMobile,
  debouncedExtendTimeframeOnZoomOutOrPan,
}: UseChartOptionsType) => {
  const [isPanAndZoomActive, setIsPanAndZoomActive] = useState<boolean>(true);
  const [activeZoomMode, setActiveZoomMode] = useState<'x' | 'xy'>('x');

  const chartRef: any = useRef(null);

  useEffect(() => {
    if (!isMobile) return;
    const chart = chartRef.current;
    const handlePinchMove = (event: any) => {
      if (event.touches.length === 2) {
        const deltaX = Math.abs(event.touches[0].pageX - event.touches[1].pageX);
        const deltaY = Math.abs(event.touches[0].pageY - event.touches[1].pageY);
        if (deltaX > deltaY) {
          chart.options.plugins.zoom.zoom.mode = 'x';
        } else {
          chart.options.plugins.zoom.zoom.mode = 'y';
        }
        chart.update('none');
      }
    };
    window.addEventListener('touchmove', handlePinchMove);
    return () => {
      window.removeEventListener('touchmove', handlePinchMove);
    };
  }, [isMobile]);

  const yAxesWithColors = useMemo(
    () => generateYAxesWithColors(tags, colors),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags, colors]
  );
  useEffect(() => {
    setOptions((prevOptions: any) => {
      const newScales = Object.keys(prevOptions.scales || {})
        .filter((key) => !key.startsWith('y'))
        .reduce((acc: Record<string, any>, key: string) => {
          acc[key] = prevOptions.scales[key];
          return acc;
        }, {});

      return {
        ...prevOptions,
        scales: {
          ...newScales,
          ...yAxesWithColors,
          x: {
            ...(newScales.x || {}),
            min: fromDate.toISOString(),
            max: toDate.toISOString(),
          },
        },
      } as any;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yAxesWithColors, fromDate, toDate]);

  const changeTimeFormatHandler = (from: Date, to: Date) => {
    const chartInstance = chartRef?.current;

    setOptions((prevOptions) => {
      const newOptions = {
        ...prevOptions,
        scales: {
          x: {
            type: 'time',
            min: from.toISOString(),
            max: to.toISOString(),
            ticks: {
              maxTicksLimit: 12,
              source: 'auto',
            },
            time: {
              displayFormats: {
                day: 'MMM dd, yyyy h:mm a',
                hour: 'MMM dd, yyyy h:mm a',
              },
            },
          },
        },
      };
      chartInstance.options = newOptions;
      chartInstance.update();
      return newOptions;
    });
  };

  const [options, setOptions] = useState({
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
      axis: 'xy',
    },
    plugins: {
      animation: {
        duration: 0,
      },
      legend: {
        position: 'top',
        align: 'start',
        display: true,

        labels: {
          boxWidth: 6,
          boxHeight: 7,
          pointStyle: 'circle',
          usePointStyle: true,
          useBorderRadius: true,
          padding: 10,
          lineHeight: 1.2,
        },
      },
      zoom: {
        enabled: true,
        zoomboxBackgroundColor: 'rgba(0, 0, 0, 0.1)',
        zoomboxBorderColor: 'rgba(0, 0, 0, 0.5)',
        zoomButtonText: null,
        zoomButtonClass: null,
        pan: {
          enabled: isPanAndZoomActive,
          mode: 'xy',
          speed: 10,
          onPan: ({ chart }: { chart: any }) => {
            if (chartRef) {
              const { min, max } = chart.scales.x;
              debouncedExtendTimeframeOnZoomOutOrPan(min, max);
            }
          },
        },
        zoom: {
          wheel: {
            enabled: !isMobile && isPanAndZoomActive,
          },
          drag: {
            backgroundColor: 'rgba(99, 115, 129, 0.5)',
            color: 'rgba(99, 115, 129, 0.5)',
            enabled: !isPanAndZoomActive,
          },
          pinch: {
            enabled: isPanAndZoomActive,
            mode: 'xy',
          },

          mode: activeZoomMode,
          speed: 10,
          onZoom: ({ chart }: { chart: any }) => {
            const { min, max } = chart.scales.x;
            debouncedExtendTimeframeOnZoomOutOrPan(min, max);
          },
        },
      },
    },
    scales: {
      x: {
        type: 'time',
        min: fromDate.toISOString(),
        max: toDate.toISOString(),
        ticks: {
          maxTicksLimit: 12,
          source: 'auto',
        },
        time: {
          displayFormats: {
            day: 'MMM dd, yyyy h:mm a',
            hour: 'MMM dd, yyyy h:mm a',
          },
        },
      },
    },
  });

  useEffect(() => {
    const chartInstance = chartRef?.current;
    const zoomLevel = chartInstance?.getZoomLevel();
    setOptions((prevOptions) => {
      const newOptions = {
        ...prevOptions,
        plugins: {
          ...prevOptions.plugins,
          zoom: {
            ...prevOptions.plugins.zoom,
            pan: {
              ...prevOptions.plugins.zoom.pan,
              enabled: false,
            },
            zoom: {
              ...prevOptions.plugins.zoom.zoom,
              wheel: {
                ...prevOptions.plugins.zoom.zoom.wheel,
                enabled: false,
              },
              drag: {
                backgroundColor: 'rgba(99, 115, 129, 0.5)',
                color: 'rgba(99, 115, 129, 0.5)',
                enabled: true,
                mode: 'x',
              },
              pinch: {
                ...prevOptions.plugins.zoom.zoom.pinch,
                enabled: false,
              },
              level: zoomLevel,
            },
          },
        },
      };
      chartInstance.options = newOptions;
      chartInstance.update();
      return newOptions;
    });

    setIsPanAndZoomActive(false);
  }, []);

  const togglePanAndZoom = () => {
    const chartInstance = chartRef?.current;
    const zoomLevel = chartInstance?.getZoomLevel();

    setIsPanAndZoomActive((prevState) => {
      const newState = !prevState;

      if (chartRef && chartRef.current) {
        chartRef.current.canvas.style.touchAction = newState ? 'none' : 'unset';
      }

      return newState;
    });

    setOptions((prevOptions) => {
      const newOptions = {
        ...prevOptions,
        plugins: {
          ...prevOptions.plugins,
          zoom: {
            ...prevOptions.plugins.zoom,
            pan: {
              ...prevOptions.plugins.zoom.pan,
              enabled: !prevOptions.plugins.zoom.pan.enabled,
            },
            zoom: {
              ...prevOptions.plugins.zoom.zoom,
              wheel: {
                ...prevOptions.plugins.zoom.zoom.wheel,
                enabled: !prevOptions.plugins.zoom.zoom.wheel.enabled,
              },
              drag: {
                ...prevOptions.plugins.zoom.pan,
                enabled: !prevOptions.plugins.zoom.zoom.drag.enabled,
                backgroundColor: 'rgba(99, 115, 129, 0.5)',
                color: 'rgba(99, 115, 129, 0.5)',
              },
              pinch: {
                ...prevOptions.plugins.zoom.zoom.pinch,
                enabled: !prevOptions.plugins.zoom.zoom.pinch.enabled,
              },
              level: zoomLevel,
            },
          },
        },
      };
      chartInstance.options = newOptions;
      chartInstance.update();
      return newOptions;
    });
  };

  const toggleModeZoomOption = (mode: 'x' | 'xy') => {
    const chartInstance = chartRef?.current;
    setActiveZoomMode(mode);

    setOptions((prevOptions) => {
      const newOptions = {
        ...prevOptions,
        plugins: {
          ...prevOptions.plugins,
          zoom: {
            ...prevOptions.plugins.zoom,
            zoom: {
              ...prevOptions.plugins.zoom.zoom,
              mode: mode,
            },
          },
        },
      };
      chartInstance.options = newOptions;
      chartInstance.update();
      return newOptions;
    });
  };

  const onResetZoomHandler = () => {
    if (chartRef) {
      chartRef.current.resetZoom();
    }
  };

  const zoomInHandler = () => {
    if (chartRef) {
      chartRef.current.zoom(1.2);
    }
  };

  const zoomOutHandler = () => {
    if (chartRef) {
      chartRef.current.zoom(0.8);
    }
  };

  return {
    chartRef,
    onResetZoomHandler,
    zoomInHandler,
    zoomOutHandler,
    isPanAndZoomActive,
    togglePanAndZoom,
    options,
    changeTimeFormatHandler,
    toggleModeZoomOption,
    activeZoomMode,
  };
};
